/**
 * The composition root's port registry.
 *
 * Every capability the app reaches for is declared here as a port instance and
 * handed down through one context. Features never construct an adapter and
 * never touch a browser global: they ask for `useClock()` and receive whatever
 * this provider was given, which is the real adapter in the app and a
 * `@ff/domain/ports/fakes` double in a test. That substitutability is the whole
 * reason the registry exists — a feature that imported `BrowserClock` directly
 * would be untestable without a real `performance` timeline.
 *
 * The bundle is split into two halves for an honest reason rather than a
 * stylistic one. `AppPorts` holds the ports whose browser adapters exist today
 * (`apps/web/src/platform/**`, P20), plus `digest` — whose implementation is
 * not a browser adapter at all. P18's `createMediaDigest` is a pure streaming
 * SHA-256 in `@ff/store`, precisely because `crypto.subtle` has no incremental
 * form, so there is nothing for a later packet to supply and nothing gained by
 * declaring it absent. `PendingAppPorts` holds the ones whose adapters really
 * are owned by later packets — cue output and announcements (P19), device
 * verification (P21), recording (P22). Declaring those as
 * required would force this file to supply something, and the only somethings
 * available are a fake (a test double shipped to users) or a throwing stub
 * (a lie the type system would then vouch for). Optional is the truthful shape:
 * `useOptionalCues()` lets a caller degrade, and `useCues()` fails loudly at the
 * point of use with a message naming what is missing.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import type {
  AnnouncerPort,
  ClockPort,
  CueOutputPort,
  DeviceVerificationPort,
  DigestPort,
  ExportPort,
  IdPort,
  KeyValuePort,
  LeaderElectionPort,
  RecorderPort,
  SceneStoragePort,
  TickerPort,
  VisibilityPort,
  WakeLockPort,
} from "@ff/domain/ports";

/** The ports the composition root can supply from a real adapter today. */
export interface AppPorts {
  readonly clock: ClockPort;
  readonly ticker: TickerPort;
  readonly visibility: VisibilityPort;
  readonly wakeLock: WakeLockPort;
  readonly leaderElection: LeaderElectionPort;
  /** The §3.1 preference keys. Cross-tab observable. */
  readonly preferences: KeyValuePort;
  /** Per-tab UI position. Deliberately not observable across documents. */
  readonly sceneStorage: SceneStoragePort;
  readonly exporter: ExportPort;
  /**
   * The document's one id source (`apps/web/src/platform/identity.ts`).
   *
   * Here rather than in a context of its own because minting an id is a
   * capability in exactly the sense the clock is, and a second registry for one
   * capability is a second answer to "which source did this id come from" — the
   * question `browserPorts.ts` exists to have exactly one answer to. It also
   * means a test that takes `withFakePorts()` gets deterministic ids without
   * knowing it needed to ask, instead of a fake bundle whose ids quietly come
   * from the real `crypto`.
   */
  readonly ids: IdPort;
  /**
   * Attachment integrity. Platform-free by construction — see
   * `@ff/store`'s `media/digest.ts` for why `crypto.subtle` cannot supply it.
   */
  readonly digest: DigestPort;
}

/**
 * Ports whose adapters land in a later packet. Absent means "this build cannot
 * do that yet", which is a state a caller can branch on — not an error state,
 * and never something this file papers over.
 */
export interface PendingAppPorts {
  readonly cues?: CueOutputPort;
  readonly announcer?: AnnouncerPort;
  readonly deviceVerification?: DeviceVerificationPort;
  readonly recorder?: RecorderPort;
}

export type PortBundle = AppPorts & PendingAppPorts;

const PortsContext = createContext<PortBundle | null>(null);

/**
 * Missing provider is an error rather than a lazily constructed default. A
 * default would build a second set of adapters — a second clock, a second
 * visibility subscription, a second leader claim — and the duplicates would be
 * invisible until two of them disagreed.
 */
export function usePorts(): PortBundle {
  const bundle = useContext(PortsContext);
  if (bundle === null) {
    throw new Error(
      "usePorts() requires an enclosing <PortProvider>. Wrap the tree in one, " +
        "or render the surface inside the test harness that installs fake ports.",
    );
  }
  return bundle;
}

/**
 * Turns "the adapter for this has not shipped yet" into a message that names
 * the port, rather than a `TypeError` on a method of `undefined` several frames
 * away from the surface that asked for it.
 */
function requirePort<T>(port: T | undefined, name: keyof PendingAppPorts): T {
  if (port === undefined) {
    throw new Error(
      `The "${name}" port was not provided to <PortProvider>. Supply it, or call ` +
        `the useOptional… variant if this surface can run without it.`,
    );
  }
  return port;
}

export function useClock(): ClockPort {
  return usePorts().clock;
}

export function useTicker(): TickerPort {
  return usePorts().ticker;
}

export function useVisibility(): VisibilityPort {
  return usePorts().visibility;
}

export function useWakeLock(): WakeLockPort {
  return usePorts().wakeLock;
}

export function useLeaderElection(): LeaderElectionPort {
  return usePorts().leaderElection;
}

export function usePreferenceStore(): KeyValuePort {
  return usePorts().preferences;
}

export function useSceneStorage(): SceneStoragePort {
  return usePorts().sceneStorage;
}

export function useExporter(): ExportPort {
  return usePorts().exporter;
}

export function useIds(): IdPort {
  return usePorts().ids;
}

export function useCues(): CueOutputPort {
  return requirePort(usePorts().cues, "cues");
}

export function useOptionalCues(): CueOutputPort | null {
  return usePorts().cues ?? null;
}

export function useAnnouncer(): AnnouncerPort {
  return requirePort(usePorts().announcer, "announcer");
}

export function useOptionalAnnouncer(): AnnouncerPort | null {
  return usePorts().announcer ?? null;
}

export function useDeviceVerification(): DeviceVerificationPort {
  return requirePort(usePorts().deviceVerification, "deviceVerification");
}

export function useOptionalDeviceVerification(): DeviceVerificationPort | null {
  return usePorts().deviceVerification ?? null;
}

export function useRecorder(): RecorderPort {
  return requirePort(usePorts().recorder, "recorder");
}

export function useOptionalRecorder(): RecorderPort | null {
  return usePorts().recorder ?? null;
}

export function useDigest(): DigestPort {
  return usePorts().digest;
}

export interface PortProviderProps {
  readonly ports: PortBundle;
  readonly children?: ReactNode;
}

/**
 * Placed exactly once, by `App`. Nesting a second provider is legitimate only
 * in a test that wants to swap one port for a subtree; the inner bundle wins
 * wholesale rather than merging, because a partial merge would let a test think
 * it replaced the clock while a stale ticker kept firing against the old one.
 */
export function PortProvider({ ports, children }: PortProviderProps): ReactNode {
  // The bundle is usually a stable object already; memoising on the field
  // identities means a caller that rebuilds the literal each render does not
  // re-render every consumer of the context.
  const value = useMemo<PortBundle>(
    () => ports,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- field identities, not the literal
    [
      ports.clock,
      ports.ticker,
      ports.visibility,
      ports.wakeLock,
      ports.leaderElection,
      ports.preferences,
      ports.sceneStorage,
      ports.exporter,
      ports.ids,
      ports.digest,
      ports.cues,
      ports.announcer,
      ports.deviceVerification,
      ports.recorder,
    ],
  );
  return <PortsContext.Provider value={value}>{children}</PortsContext.Provider>;
}
